import { DOMSerializer } from "prosemirror-model";
import React from "react";
import { isElementDomNode } from "../core/dom-utils";
export class ReactNodeView {
    static create(config) {
        return (node, view, getPos) => {
            return new ReactNodeView(node, view, getPos, config);
        };
    }
    constructor(node, view, getPos, config) {
        var _a;
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.config = config;
        this.Component = () => {
            const ReactComponent = this.config.component;
            return (React.createElement(ReactComponent, { view: this.view, attrs: this.node.attrs, getPos: this.getPos, forwardRef: this.forwardRef, ...this.config.extraProps }));
        };
        this.forwardRef = (node) => {
            if (!node) {
                return;
            }
            node.append(this.contentDOMWrapper);
        };
        this.dom = this.createDom();
        const { wrapper, contentDOM } = (_a = this.createContentDom()) !== null && _a !== void 0 ? _a : {};
        this.contentDOMWrapper = wrapper;
        this.contentDOM = contentDOM;
        if (this.contentDOMWrapper) {
            this.dom.append(this.contentDOMWrapper);
        }
        this.renderComponent();
    }
    update(node) {
        if (this.node.type !== node.type) {
            return false;
        }
        this.node = node;
        this.renderComponent();
        return true;
    }
    destroy() {
        this.config.portalContainer.remove(this.dom);
    }
    renderComponent() {
        this.config.portalContainer.render(this.dom, this.Component);
    }
    /**
     * Create the dom element which will hold the react component.
     */
    createDom() {
        const element = document.createElement(this.node.isInline ? "span" : "div");
        // ProseMirror breaks down when it encounters multiple nested empty
        // elements. This class prevents this from happening.
        element.classList.add(`${this.node.type.name}-node-view-wrapper`);
        return element;
    }
    /**
     * @remarks From remirror's `react-node-view.tsx`
     */
    createContentDom() {
        var _a, _b;
        if (this.node.isLeaf) {
            return;
        }
        const domSpec = (_b = (_a = this.node.type.spec).toDOM) === null || _b === void 0 ? void 0 : _b.call(_a, this.node);
        // Only allow content if a domSpec exists which is used to render the content.
        if (!domSpec) {
            return;
        }
        // Let `ProseMirror` interpret the domSpec returned by `toDOM` to provide
        // the dom and `contentDOM`.
        const { contentDOM, dom } = DOMSerializer.renderSpec(document, domSpec);
        // The content dom needs a wrapper node in react since the dom element which
        // it renders inside isn't immediately mounted.
        let wrapper;
        if (!isElementDomNode(dom)) {
            return;
        }
        // Default to setting the wrapper to a different element.
        wrapper = dom;
        if (dom === contentDOM) {
            wrapper = document.createElement("span");
            wrapper.classList.add(`${this.node.type.name}-node-view-content-wrapper`);
            wrapper.append(contentDOM);
        }
        return { wrapper, contentDOM };
    }
    /**
     * The handler which decides when mutations should be ignored.
     *
     * @remarks From remirror's `react-node-view.tsx`
     */
    ignoreMutation(mutation) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (mutation.type === "selection") {
            // If a node type is unselectable, then ignore all selection mutations.
            return !this.node.type.spec.selectable;
        }
        if (!this.contentDOMWrapper) {
            return true;
        }
        return !this.contentDOMWrapper.contains(mutation.target);
    }
}
